import { useState } from 'react'
import { Screen } from '@/components/layout'
import { Badge, Button } from '@/components/primitives'
import { Amount, DetailRow, DetailRows, SummaryCard } from '@/components/cards'
import { CategoryPicker } from '@/components/pickers'
import { List, LinkRow, ToggleRow } from '@/components/rows'
import { ErrorState, LoadingRegion, SkeletonCard } from '@/components/states'
import type { CategoryId, Transaction } from '@/generated/types'
import { ApiFailure, api } from '@/lib/api'
import { useAnalysisStatus, type AnalysisState } from '@/lib/analysis'
import { t } from '@/lib/copy'
import { feeKindName } from '@/lib/display'
import { formatDate, formatMoney } from '@/lib/format'
import { useNavigate, useRoute } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import { bridge } from '@/lib/bridge'

/**
 * Recategorization applies optimistically and rolls back on failure, per the
 * `optimistic` rule in flows.json.
 *
 * This screen renders no prose, and it still gets both new states — because
 * this is where a model-placed category is SEEN and corrected. Here they mean
 * categorization, not wording: `analysisPending` is a row whose merchant is
 * queued for inference, `analysisFailed` is a row that stayed uncategorized.
 * Neither blocks anything; the picker works identically in both.
 */
export function TransactionDetailScreen() {
  const route = useRoute()
  const navigate = useNavigate()
  const id = route.params.id

  const transaction = useAsync((signal) => api.transactions.get(id, signal), [id])
  /**
   * The whole account list, not GET /accounts/{id} once the transaction names
   * one. Waiting for the id chains two requests, and through the edge a chain
   * pays the round trip once per link — the single client behaviour the latency
   * budget in edge-proxy.json turns on. The list is small, the account row is
   * one line of this screen, and both requests now leave together.
   */
  const accounts = useAsync((signal) => api.accounts.list(signal), [])
  const accountName = accounts.state.data?.find((a) => a.id === transaction.state.data?.accountId)?.name

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)
  const [saved, setSaved] = useState(false)

  const tx = transaction.state.data

  // Only a row the rules and the model both left unplaced has anything
  // outstanding, so only that row polls — x-polling forbids the rest.
  const uncategorized = tx?.category === 'uncategorized'
  const analysis = useAnalysisStatus({ enabled: uncategorized, onGenerated: transaction.reload })

  // `analysisPending` is a merchant queued for inference. `analysisFailed` is
  // every other way a row is left unplaced — generation off, generation failed,
  // or generation finished and still placed nothing. A visible gap beats a
  // confident wrong answer, so the row simply stays uncategorized and the
  // picker below behaves exactly as it does in `ready`.
  const categorization: AnalysisState = !uncategorized
    ? null
    : analysis.state === 'analysisPending'
      ? 'analysisPending'
      : 'analysisFailed'

  const dataState = saving
    ? 'saving'
    : error
      ? 'error'
      : transaction.state.status === 'loading' && !tx
        ? 'loading'
        : transaction.state.status === 'error'
          ? 'error'
          : 'ready'

  const screenState = dataState === 'ready' ? (categorization ?? 'ready') : dataState

  type Patch = Parameters<typeof api.transactions.update>[1]

  const patch = async (changes: Patch, optimistic: Partial<Transaction>) => {
    if (!tx) return
    const previous = tx
    transaction.setData({ ...tx, ...optimistic })
    setSaving(true)
    setError(null)
    try {
      const updated = await api.transactions.update(id, changes)
      transaction.setData(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      // Roll back to exactly what the Server last told us.
      transaction.setData(previous)
      bridge.reportError(`failed to save change to transaction ${id}`, 'transactionDetail')
      setError(e instanceof ApiFailure ? e : null)
    } finally {
      setSaving(false)
    }
  }

  if (screenState === 'loading') {
    return (
      <Screen id="transactionDetail" state={screenState} back={{ screen: 'transactions' }}>
        <LoadingRegion>
          <SkeletonCard />
          <SkeletonCard />
        </LoadingRegion>
      </Screen>
    )
  }

  if (!tx) {
    return (
      <Screen id="transactionDetail" state="error" back={{ screen: 'transactions' }}>
        <ErrorState
          titleKey="transactionDetail.error.title"
          error={transaction.state.status === 'error' ? transaction.state.error : undefined}
          onRetry={transaction.reload}
        />
      </Screen>
    )
  }

  return (
    <Screen
      id="transactionDetail"
      state={screenState}
      title={tx.merchant ?? tx.description}
      back={{ screen: 'transactions' }}
    >
      <div className="stack">
        {error ? (
          <ErrorState titleKey="transactionDetail.error.title" error={error} onRetry={() => setError(null)} />
        ) : null}

        <SummaryCard>
          <div className="stack-sm">
            <span className="metric-label">{formatDate(tx.date)}</span>
            <span style={{ fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)' }}>
              <Amount money={tx.amount} signed />
            </span>
            {saved ? <span className="caption positive">{t('transactionDetail.saved')}</span> : null}
          </div>
        </SummaryCard>

        <div className="card">
          <DetailRows>
            {tx.merchant ? <DetailRow label={t('transactionDetail.merchant')}>{tx.merchant}</DetailRow> : null}
            <DetailRow label={t('transactionDetail.date')}>{formatDate(tx.date)}</DetailRow>
            {tx.postedDate ? (
              <DetailRow label={t('transactionDetail.postedDate')}>{formatDate(tx.postedDate)}</DetailRow>
            ) : null}
            <DetailRow label={t('transactionDetail.account')}>{accountName ?? '—'}</DetailRow>
            <DetailRow label={t('transactionDetail.description')}>{tx.description}</DetailRow>
            {tx.balanceAfter ? (
              <DetailRow label={t('accountDetail.balance')}>{formatMoney(tx.balanceAfter)}</DetailRow>
            ) : null}
          </DetailRows>
        </div>

        <div className="card stack">
          <CategoryPicker
            value={tx.category}
            label={t('transactionDetail.category')}
            onChange={(category: CategoryId) =>
              void patch({ category }, { category, categorySource: 'user' })
            }
          />
          {/* A category the model placed is marked as a suggestion and can be
              accepted in one tap, which rewrites it as `user` — the precedence
              the contract pins and never overwrites. Correctable, which is
              actionable; it is not a note about how the row got its label. */}
          {tx.categorySource === 'model' ? (
            <div className="row">
              <Badge>{t('transactionDetail.category.suggested')}</Badge>
              <Button
                variant="secondary"
                disabled={saving}
                onClick={() => void patch({ category: tx.category }, { categorySource: 'user' })}
              >
                {t('transactionDetail.category.confirm')}
              </Button>
            </div>
          ) : null}
          {tx.merchantKey ? (
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() =>
                void patch(
                  { category: tx.category, applyToMerchant: true },
                  { categorySource: 'user' },
                )
              }
            >
              {t('transactionDetail.cta.applyToAll', { merchant: tx.merchant ?? tx.description })}
            </Button>
          ) : null}
        </div>

        <List>
          <ToggleRow
            label={t('transactionDetail.recurring.toggle')}
            checked={tx.isRecurring ?? false}
            onChange={(isRecurring) => void patch({ isRecurring }, { isRecurring })}
          />
          {tx.recurringSeriesId ? (
            <LinkRow
              label={t('transactionDetail.recurring.toggle')}
              value={t('transactionDetail.recurring.link', { name: tx.merchant ?? tx.description })}
              onSelect={() => navigate('recurringDetail', { id: tx.recurringSeriesId! })}
            />
          ) : null}
          <ToggleRow
            label={t('transactionDetail.fee.toggle')}
            checked={tx.isFee ?? false}
            onChange={(isFee) => void patch({ isFee }, { isFee })}
          />
          {tx.isFee ? (
            <LinkRow
              label={t('transactionDetail.fee.toggle')}
              value={feeKindName(tx.feeKind)}
              onSelect={() => navigate('categoryDetail', { id: 'fees' })}
            />
          ) : null}
        </List>

        <Button variant="quiet" onClick={() => navigate('categoryDetail', { id: tx.category })}>
          {t('transactionDetail.cta.changeCategory')}
        </Button>
      </div>
    </Screen>
  )
}
